import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { accountingRequest, formatMoney, jsonInit, round2 } from '../api'
import { useLoggedInName } from '../loggedInUser'

// SND-14275: Process Payment (charge a card) and Refund (a card payment Intair charged) through the Adyen wrapper.
// Control Center calls tbi-gg-payment-api itself, so the card number goes from this page to the payment API and never
// to Leo; Leo then records the approved result (a credit for a charge, a debit for a refund) with the transaction id.

const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_URL || ''

// Reasons until Michelle sends the final list.
export const CARD_REASONS = ['Fare difference', 'Change fee', 'Cancellation', 'Service fee', 'Schedule change', 'Other']

type Mode = 'charge' | 'refund'

// Card type from the number: the wrapper's cctype and Leo's FoP code.
function cardKind(number: string) {
  if (/^4/.test(number)) return { cctype: 'VI', fop: 'VI' }
  if (/^(5[1-5]|2[2-7])/.test(number)) return { cctype: 'MC', fop: 'CA' }
  if (/^3[47]/.test(number)) return { cctype: 'AX', fop: 'AX' }
  return null
}

function luhn(number: string) {
  let sum = 0
  let double = false
  for (let index = number.length - 1; index >= 0; index -= 1) {
    let digit = Number(number[index])
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    double = !double
  }
  return number.length >= 13 && sum % 10 === 0
}

type Card = { number: string; expiryMonth: string; expiryYear: string; cvv: string }
const EMPTY_CARD: Card = { number: '', expiryMonth: '', expiryYear: '', cvv: '' }

function cardProblem(card: Card) {
  if (!luhn(card.number) || !cardKind(card.number)) return 'Enter a valid Visa, Mastercard or Amex card number.'
  const month = Number(card.expiryMonth)
  if (!/^\d{2}$/.test(card.expiryMonth) || month < 1 || month > 12) return 'Expiry month must be 01 to 12.'
  if (!/^\d{2}$/.test(card.expiryYear)) return 'Expiry year must be 2 digits.'
  const now = new Date()
  const year = 2000 + Number(card.expiryYear)
  if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) return 'The card has expired.'
  if (!/^\d{3,4}$/.test(card.cvv)) return 'CVV must be 3 or 4 digits.'
  return ''
}

function CardFields({ card, onChange }: { card: Card; onChange: (card: Card) => void }) {
  const set = (key: keyof Card, value: string, max: number) => onChange({ ...card, [key]: value.replace(/\D/g, '').slice(0, max) })
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, gridColumn: '1 / -1' }}>
      <TextField
        size="small"
        label="Card number"
        value={card.number}
        onChange={(event) => set('number', event.target.value, 19)}
        slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
        sx={{ gridColumn: '1 / -1' }}
      />
      <TextField size="small" label="Expiry month (MM)" value={card.expiryMonth} onChange={(event) => set('expiryMonth', event.target.value, 2)} slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }} />
      <TextField size="small" label="Expiry year (YY)" value={card.expiryYear} onChange={(event) => set('expiryYear', event.target.value, 2)} slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }} />
      <TextField size="small" label="CVV" value={card.cvv} onChange={(event) => set('cvv', event.target.value, 4)} slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }} />
    </Box>
  )
}

async function callPaymentApi(operation: string, payload: any, agency: string) {
  if (!PAYMENT_API_URL) throw new Error('The payment service is not set up for this environment.')
  const response = await fetch(`${PAYMENT_API_URL}/${operation}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', agency: agency || 'LEO', channel: 'B2B' },
    body: JSON.stringify(payload),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(body?.message || 'The payment service did not answer.')
  const result = body?.transaction?.[operation] || {}
  // Approved when the wrapper gives a transaction id and no error (same rule as flight-core).
  if (!String(result.transactionId || '').trim() || String(result.error || '').trim()) {
    throw new Error(`Adyen declined it: ${result.responseDesc || result.error || 'no transaction id'}${result.responseCode ? ` (code ${result.responseCode})` : ''}.`)
  }
  return result
}

export default function CardPaymentDialog({
  open,
  mode,
  invoice,
  payment,
  onClose,
  onSaved,
}: {
  open: boolean
  mode: Mode
  invoice: any
  payment?: any
  onClose: () => void
  onSaved: (invoice: any) => void
}) {
  const loggedIn = useLoggedInName()
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState('')
  const [refundType, setRefundType] = useState<'existing' | 'new'>('existing')
  const [card, setCard] = useState<Card>({ ...EMPTY_CARD })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const left = payment ? round2(Number(payment.credit || 0) - Number(payment.refunded || 0)) : 0
  const currency = invoice?.flightType === 'USD' ? 'USD' : 'CAD'
  const existing = mode === 'refund' && refundType === 'existing' && Boolean(payment?.gatewayTransactionId)

  useEffect(() => {
    if (!open) return
    setReason('')
    setAmount(mode === 'refund' && left > 0 ? left.toFixed(2) : '')
    setRefundType(payment?.gatewayTransactionId ? 'existing' : 'new')
    setCard({ ...EMPTY_CARD })
    setError('')
  }, [open, mode, payment, left])

  async function submit(event: any) {
    event.preventDefault()
    const value = Number(amount)
    if (!reason) return setError('Choose a reason.')
    if (!(value > 0) || !/^\d+(\.\d{1,2})?$/.test(amount)) return setError('Amount must be more than 0 with up to 2 decimals.')
    if (existing && value > left) return setError(`The refund is more than the ${formatMoney(left, currency)} left to refund.`)
    const kind = mode === 'charge' ? 'purchase' : existing ? 'refund' : 'independentRefund'
    if (kind !== 'refund') {
      const problem = cardProblem(card)
      if (problem) return setError(problem)
    }
    setBusy(true)
    setError('')
    const bookingId = `Invoice${invoice.invoiceNumber}`
    const agency = invoice?.agency?.code || ''
    let result: any
    try {
      if (kind === 'refund') {
        result = await callPaymentApi('refund', { bookingId, transactionId: payment.gatewayTransactionId, amount: value, currency }, agency)
      } else {
        const type = cardKind(card.number)
        result = await callPaymentApi(kind, {
          bookingId,
          amount: value,
          currency,
          creditCard: { number: card.number, type: type?.cctype, expiryMonth: card.expiryMonth, expiryYear: card.expiryYear, cvv: card.cvv },
        }, agency)
      }
    } catch (err: any) {
      setError(err.message)
      setBusy(false)
      return
    }
    try {
      const type = cardKind(card.number)
      const updated = await accountingRequest(
        `/invoices/${encodeURIComponent(invoice.recordLocator)}/card-transactions`,
        jsonInit('POST', {
          kind,
          amount: value,
          reason,
          by: loggedIn || 'Control Center',
          refundOfPaymentId: kind === 'refund' ? payment.id : undefined,
          fopCode: kind === 'refund' ? undefined : type?.fop,
          cardFirst6: kind === 'refund' ? undefined : card.number.slice(0, 6),
          cardLast4: kind === 'refund' ? undefined : card.number.slice(-4),
          gateway: { transactionId: result.transactionId, refNo: result.refNo, authCode: result.authCode, orderId: result.orderId, responseCode: result.responseCode },
        }),
      )
      setCard({ ...EMPTY_CARD })
      onSaved(updated)
      onClose()
    } catch (err: any) {
      // Adyen already processed it: say so, with the id, so it can be entered by hand.
      setError(`Adyen processed it (transaction ${result.transactionId}), but Leo could not record it: ${err.message} Add it as a client transaction by hand.`)
    } finally {
      setBusy(false)
    }
  }

  const title = mode === 'charge' ? 'Process Payment' : 'Refund'
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {title}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Invoice {invoice?.invoiceNumber}
            {mode === 'refund' && payment ? ` · ${payment.fopCode} ${payment.cardOrCheckDisplay || ''} · ${formatMoney(left, currency)} left to refund` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField select required size="small" label="Reason" value={reason} onChange={(event) => setReason(event.target.value)}>
              {CARD_REASONS.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
            <TextField
              required
              size="small"
              type="number"
              label={`Amount (${currency})`}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              slotProps={{ htmlInput: { step: '0.01', min: '0.01' } }}
            />
            {mode === 'refund' ? (
              <RadioGroup row value={refundType} onChange={(event) => setRefundType(event.target.value as 'existing' | 'new')} sx={{ gridColumn: '1 / -1' }}>
                <FormControlLabel value="existing" control={<Radio size="small" />} label="Refund existing transaction" disabled={!payment?.gatewayTransactionId} />
                <FormControlLabel value="new" control={<Radio size="small" />} label="New refund" />
              </RadioGroup>
            ) : null}
            {existing ? (
              <TextField size="small" label="Transaction Id" value={payment.gatewayTransactionId} slotProps={{ input: { readOnly: true } }} sx={{ gridColumn: '1 / -1' }} />
            ) : (
              <CardFields card={card} onChange={setCard} />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" color="secondary" disabled={busy}>
            {busy ? 'Processing…' : 'Submit'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
